import * as THREE from 'three';
import { PRNG } from 'renderoni';
import { KITCHEN, type EnvironmentDef, type LandmarkRule, type PropModel } from '../config/environments';
import { CHUNK_LENGTH } from '../sim/terrain/chunks';
import type { TerrainProfile } from '../sim/terrain/profile';
import { InstancedModel, type GameModels, type ModelBatch } from './assets';

type SignIcon = 'drop' | 'jump';

interface Placement {
  model: PropModel | 'sign';
  x: number;
  z: number;
  scale: number;
  yaw: number;
  /** Follow the ground slope (fences, hedges); other props stand upright. */
  tilt: boolean;
  icon?: SignIcon;
  /** Index of the landmark rule whose signboard text this building carries. */
  landmark?: number;
  /** The start-line kitchen (stands on its lowest footing like the landmarks). */
  kitchen?: boolean;
}

/** Chunks alive at once (streaming window plus the run-up), used to size instance buffers. */
const MAX_CHUNKS = 7;
/** Warning signs stand this far before a drop or jump so the player can react. */
const SIGN_LEAD = 18;
const SIGN_Z = -1.4;
/** Signs are scaled up so the icon reads at the gameplay camera distance. */
const SIGN_SCALE = 1.4;
/** Icon centre on the sign face, in sign-local metres (plate centre, just in front of it). */
const ICON_Y = 2.02;
const ICON_Z = 0.1;
/** The Dapur MBG kitchen stands at the start line (x = KITCHEN.offset) in every environment. */
const KITCHEN_X = KITCHEN.offset;

/**
 * Decorative roadside kit for an environment (Kiln GLBs, instanced): scattered props, rows that
 * follow the ground, landmark buildings (schools, Kopdes Merah Putih, warung) with painted signs, and
 * warning signs before drops and jumps. Placement is a pure function of the environment seed and the
 * chunk index, so every run and every screenshot shows the same scenery. Presentation only.
 */
export class RoadsideProps {
  readonly group = new THREE.Group();
  private readonly models = new Map<PropModel | 'sign', InstancedModel>();
  private readonly capacity = new Map<PropModel | 'sign', number>();
  private readonly chunks = new Map<number, Placement[]>();
  private readonly matrix = new THREE.Matrix4();
  private readonly quat = new THREE.Quaternion();
  private readonly euler = new THREE.Euler();
  private readonly pos = new THREE.Vector3();
  private readonly scale = new THREE.Vector3();
  private dirty = false;
  private readonly kitchenSign: THREE.Mesh;
  private readonly icons: Record<SignIcon, THREE.InstancedMesh>;
  /** One instanced text plane per landmark rule that has a signboard. */
  private readonly landmarkSigns: (THREE.InstancedMesh | null)[];

  constructor(
    models: GameModels,
    props: Partial<Record<PropModel, ModelBatch[]>>,
    private readonly terrain: TerrainProfile,
    private readonly env: EnvironmentDef,
  ) {
    const add = (model: PropModel | 'sign', batches: ModelBatch[] | undefined, capacity: number) => {
      if (!batches) throw new Error(`Prop model not loaded: ${model}`);
      this.capacity.set(model, (this.capacity.get(model) ?? 0) + capacity);
    };
    for (const r of env.props) add(r.model, props[r.model], r.perChunk[1] * MAX_CHUNKS);
    if (env.rows) add(env.rows.model, props[env.rows.model], Math.ceil(CHUNK_LENGTH / env.rows.pitch + 1) * MAX_CHUNKS);
    for (const l of env.landmarks) add(l.model, props[l.model], 3);
    add('dapur', props.dapur, 1);
    add('sign', models.warningSign, 12);
    for (const [model, capacity] of this.capacity) {
      const batches = model === 'sign' ? models.warningSign : props[model]!;
      this.models.set(model, new InstancedModel(batches, capacity, this.group));
    }
    this.icons = {
      drop: iconMesh(drawDropIcon, 12, this.group),
      jump: iconMesh(drawJumpIcon, 12, this.group),
    };
    this.landmarkSigns = env.landmarks.map((l) => {
      if (!l.sign) return null;
      const mesh = new THREE.InstancedMesh(
        new THREE.PlaneGeometry(l.sign.width, l.sign.height),
        new THREE.MeshBasicMaterial({ map: signTexture(l.sign.text, l.sign.color, l.sign.ink, l.sign.width / l.sign.height) }),
        3,
      );
      mesh.frustumCulled = false;
      mesh.count = 0;
      this.group.add(mesh);
      return mesh;
    });
    // The kitchen's signboard (plain text; the real SPPG emblem is never reproduced).
    const ks = KITCHEN.sign!;
    this.kitchenSign = new THREE.Mesh(new THREE.PlaneGeometry(ks.width, ks.height), new THREE.MeshBasicMaterial({ map: signTexture(ks.text, ks.color, ks.ink, ks.width / ks.height) }));
    this.kitchenSign.position.set(KITCHEN_X, this.footingY(KITCHEN_X, KITCHEN.halfWidth) + ks.y, KITCHEN.z + ks.z + 0.01);
    this.group.add(this.kitchenSign);
  }

  addChunk(index: number): void {
    if (this.chunks.has(index)) return;
    this.chunks.set(index, this.place(index));
    this.dirty = true;
  }

  removeChunk(index: number): void {
    if (this.chunks.delete(index)) this.dirty = true;
  }

  clear(): void {
    this.chunks.clear();
    this.dirty = true;
  }

  dispose(): void {
    this.group.traverse((node) => {
      const mesh = node as THREE.Mesh;
      // Model geometry and materials are shared through the asset cache; only own planes are freed.
      if (mesh.isMesh && mesh.geometry instanceof THREE.PlaneGeometry) {
        mesh.geometry.dispose();
        const m = mesh.material as THREE.MeshBasicMaterial;
        m.map?.dispose();
        m.dispose();
      }
    });
    this.group.removeFromParent();
  }

  /** Rebuilds instance buffers after chunks change (not every frame). */
  update(): void {
    if (!this.dirty) return;
    this.dirty = false;
    const counts = new Map<PropModel | 'sign', number>();
    const iconCounts: Record<SignIcon, number> = { drop: 0, jump: 0 };
    const signCounts = this.landmarkSigns.map(() => 0);
    for (const placements of this.chunks.values()) {
      for (const p of placements) {
        const n = counts.get(p.model) ?? 0;
        if (n >= (this.capacity.get(p.model) ?? 0)) continue;
        const y = p.kitchen
          ? this.footingY(p.x, KITCHEN.halfWidth)
          : p.landmark !== undefined
            ? this.footingY(p.x, this.env.landmarks[p.landmark].halfWidth)
            : this.terrain.heightAt(p.x);
        const roll = p.tilt ? Math.atan(this.terrain.slopeAt(p.x)) : 0;
        this.euler.set(0, p.yaw, roll);
        this.quat.setFromEuler(this.euler);
        this.pos.set(p.x, y, p.z);
        this.scale.setScalar(p.scale);
        this.models.get(p.model)!.set(n, this.matrix.compose(this.pos, this.quat, this.scale));
        counts.set(p.model, n + 1);
        if (p.icon) {
          this.pos.set(p.x, y + ICON_Y * p.scale, p.z + ICON_Z * p.scale);
          this.icons[p.icon].setMatrixAt(iconCounts[p.icon]++, this.matrix.compose(this.pos, this.quat, this.scale));
        }
        const sign = p.landmark !== undefined ? this.env.landmarks[p.landmark].sign : undefined;
        const signMesh = p.landmark !== undefined ? this.landmarkSigns[p.landmark] : null;
        if (sign && signMesh && signCounts[p.landmark!] < 3) {
          this.pos.set(p.x, y + sign.y, p.z + sign.z);
          signMesh.setMatrixAt(signCounts[p.landmark!]++, this.matrix.compose(this.pos, this.quat, this.scale));
        }
      }
    }
    for (const [model, mesh] of this.models) mesh.commit(counts.get(model) ?? 0);
    for (const icon of Object.keys(this.icons) as SignIcon[]) {
      this.icons[icon].count = iconCounts[icon];
      this.icons[icon].instanceMatrix.needsUpdate = true;
    }
    this.landmarkSigns.forEach((mesh, i) => {
      if (!mesh) return;
      mesh.count = signCounts[i];
      mesh.instanceMatrix.needsUpdate = true;
    });
    this.kitchenSign.visible = [...this.chunks.keys()].some((i) => i * CHUNK_LENGTH <= KITCHEN_X && KITCHEN_X < (i + 1) * CHUNK_LENGTH);
  }

  private place(index: number): Placement[] {
    // A separate PRNG stream per chunk: placement never depends on which chunks were visited.
    const rng = new PRNG(this.env.seed, 1000 + index + 64);
    const range = (r: [number, number]) => r[0] + rng.nextFloat() * (r[1] - r[0]);
    const x0 = index * CHUNK_LENGTH;
    const x1 = x0 + CHUNK_LENGTH;
    const out: Placement[] = [];
    const reserved: Array<[number, number]> = [];
    const clear = (x: number, margin = 1.5) => reserved.every(([c, h]) => Math.abs(x - c) > h + margin);

    if (x0 <= KITCHEN_X && KITCHEN_X < x1) {
      out.push({ model: 'dapur', x: KITCHEN_X, z: KITCHEN.z, scale: 1, yaw: 0, tilt: false, kitchen: true });
      reserved.push([KITCHEN_X, KITCHEN.halfWidth]);
    }
    this.env.landmarks.forEach((rule, i) => {
      const x = this.landmarkIn(rule, x0, x1);
      if (x === null || !clear(x, 2)) return;
      out.push({ model: rule.model, x, z: rule.z, scale: 1, yaw: 0, tilt: false, landmark: i });
      reserved.push([x, rule.halfWidth]);
    });

    // Telegraphs: a warning sign SIGN_LEAD metres before every drop and jump ramp.
    this.terrain.ensure(x1 + SIGN_LEAD + 1);
    for (const seg of this.terrain.segments) {
      if (seg.kind !== 'drop' && seg.kind !== 'kicker') continue;
      const x = seg.x0 - SIGN_LEAD;
      if (x < x0 || x >= x1 || !clear(x)) continue;
      out.push({ model: 'sign', x, z: SIGN_Z, scale: SIGN_SCALE, yaw: 0, tilt: false, icon: seg.kind === 'drop' ? 'drop' : 'jump' });
    }

    for (const rule of this.env.props) {
      const n = rule.perChunk[0] + Math.floor(rng.nextFloat() * (rule.perChunk[1] - rule.perChunk[0] + 1));
      for (let i = 0; i < n; i++) {
        const x = x0 + rng.nextFloat() * CHUNK_LENGTH;
        const z = -range(rule.z);
        const scale = range(rule.scale);
        const yaw = rule.randomYaw ? rng.nextFloat() * Math.PI * 2 : 0;
        if (clear(x)) out.push({ model: rule.model, x, z, scale, yaw, tilt: false });
      }
    }
    // Some chunks get a row (fence, tea hedge) just behind the drive line, following the ground.
    const row = this.env.rows;
    if (row && rng.nextFloat() < row.chance) {
      const pieces = 3 + Math.floor(rng.nextFloat() * 6);
      const start = x0 + rng.nextFloat() * Math.max(0, CHUNK_LENGTH - pieces * row.pitch);
      for (let s = 0; s < pieces; s++) {
        const x = start + row.pitch / 2 + s * row.pitch;
        if (x < x1 && clear(x)) out.push({ model: row.model, x, z: row.z, scale: 1, yaw: 0, tilt: true });
      }
    }
    return out;
  }

  /** Buildings stand on the lowest ground under their footprint, so no corner floats on a slope. */
  private footingY(x: number, halfWidth: number): number {
    let lo = Infinity;
    for (let dx = -halfWidth * 0.8; dx <= halfWidth * 0.8; dx += halfWidth / 4) lo = Math.min(lo, this.terrain.heightAt(x + dx));
    return lo;
  }

  /** A landmark in [x0, x1): near every `every` metres (k ≥ 1) plus `offset`, on the flattest spot. */
  private landmarkIn(rule: LandmarkRule, x0: number, x1: number): number | null {
    const k = Math.ceil((x0 - rule.offset) / rule.every);
    const target = k * rule.every + rule.offset;
    if (target < 1 || target < x0 || target >= x1) return null;
    this.terrain.ensure(x1 + rule.halfWidth + 1);
    // Flattest footprint within the chunk, judged by the height spread across the building's width.
    let best = target;
    let bestSpread = Infinity;
    for (let x = Math.max(x0, target - 20); x < Math.min(x1, target + 45); x += 1) {
      let lo = Infinity;
      let hi = -Infinity;
      for (let dx = -rule.halfWidth; dx <= rule.halfWidth; dx += rule.halfWidth / 3) {
        const h = this.terrain.heightAt(x + dx);
        lo = Math.min(lo, h);
        hi = Math.max(hi, h);
      }
      if (hi - lo < bestSpread - 0.05) {
        bestSpread = hi - lo;
        best = x;
      }
    }
    return best;
  }
}

function signTexture(text: string, color: string, ink: string, aspect: number): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.height = 128;
  canvas.width = Math.min(1024, Math.round(128 * aspect));
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = ink;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // "BIG\nsmaller": a second line is set smaller under the first, as on the real signs.
    const [first, second] = text.split('\n');
    const font = (px: number) => `800 ${px}px "Arial Narrow", "Bahnschrift", system-ui, sans-serif`;
    if (second) {
      ctx.font = font(62);
      ctx.fillText(first, canvas.width / 2, 46, canvas.width * 0.92);
      ctx.font = font(34);
      ctx.fillText(second, canvas.width / 2, 100, canvas.width * 0.92);
    } else {
      ctx.font = font(74);
      ctx.fillText(first, canvas.width / 2, 68, canvas.width * 0.92);
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
}

/** Instanced icon quads painted on the warning signs. */
function iconMesh(draw: (ctx: CanvasRenderingContext2D) => void, capacity: number, parent: THREE.Object3D): THREE.InstancedMesh {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 128;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = '#1b1b1b';
    ctx.strokeStyle = '#1b1b1b';
    ctx.lineWidth = 9;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    draw(ctx);
  }
  const map = new THREE.CanvasTexture(canvas);
  map.colorSpace = THREE.SRGBColorSpace;
  const mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(0.5, 0.5), new THREE.MeshBasicMaterial({ map, transparent: true }), capacity);
  mesh.frustumCulled = false;
  mesh.count = 0;
  parent.add(mesh);
  return mesh;
}

/** Steep descent: a slope wedge with a downhill arrow. */
function drawDropIcon(ctx: CanvasRenderingContext2D): void {
  ctx.beginPath();
  ctx.moveTo(18, 40);
  ctx.lineTo(18, 106);
  ctx.lineTo(110, 106);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(62, 26);
  ctx.lineTo(102, 62);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(104, 64);
  ctx.lineTo(80, 60);
  ctx.lineTo(100, 42);
  ctx.closePath();
  ctx.fill();
}

/** Jump: a ramp with an arcing flight arrow. */
function drawJumpIcon(ctx: CanvasRenderingContext2D): void {
  ctx.beginPath();
  ctx.moveTo(14, 108);
  ctx.lineTo(56, 108);
  ctx.lineTo(56, 84);
  ctx.closePath();
  ctx.fill();
  ctx.fillRect(84, 102, 32, 6);
  ctx.beginPath();
  ctx.moveTo(52, 76);
  ctx.quadraticCurveTo(76, 18, 104, 82);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(108, 92);
  ctx.lineTo(92, 74);
  ctx.lineTo(114, 70);
  ctx.closePath();
  ctx.fill();
}
